<template>
  <div class="client renderless-application-list">
    <slot v-bind="{ applications, loading }" v-if="applications"></slot>
    <slot name="no-data">
      <v-card
        tile
        class="text-xs-center"
        v-if="applications.length === 0 && !hasHidden"
      >
        <v-card-text>
          <v-alert
            outline
            :color="themes.client.light.tertiary"
            type="info"
            :value="!loading"
          >
            <span class="subheading" v-if="isFiltered">
              All applications in this phase have been filtered out.
            </span>
            <span class="subheading" v-else>
              There are no applications in this phase.
            </span>
          </v-alert>
        </v-card-text>
      </v-card>
    </slot>
    <slot name="footer">
      <v-card tile class="text-xs-center" v-if="hasHidden">
        <v-card-text>
          <v-alert
            :color="themes.client.light.tertiary"
            :value="hideIncomplete"
            type="info"
            outline
          >
            <span class="subheading">
              Incomplete and Ineligble applications are hidden.
            </span>
            <v-btn
              :color="themes.client.light.secondary"
              @click="hideIncomplete = false"
            >
              Show me
            </v-btn>
          </v-alert>
          <v-alert
            :color="themes.client.light.tertiary"
            :value="!hideIncomplete"
            type="info"
            outline
          >
            <span class="subheading">
              You can hide incomplete applications.
            </span>
            <v-btn
              :color="themes.client.light.secondary"
              @click="hideIncomplete = true"
            >
              Hide
            </v-btn>
          </v-alert>
        </v-card-text>
      </v-card>
    </slot>
    <v-progress-linear
      v-show="loading && loadingIndicator"
      :color="themes.client.light.input"
      class="ma-0"
      height="5"
      :indeterminate="!progress"
      :value="progress"
    />
    <slot
      name="paginate"
      v-bind="{ total, pages, page, per, from, to, paginate }"
    >
      <div class="text-xs-center mt-3">
        <v-pagination
          @input="paginate({ page: $event })"
          :color="themes.client.light.input"
          :value="Number(page)"
          :length="pages"
        />
        <div class="caption">
          <span> Showing {{ from }}-{{ to }} of {{ total }} </span>
          <span>
            <v-menu offset-y>
              <v-btn slot="activator" small color="white">
                {{ per }} per Page
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
              <v-list>
                <v-list-tile
                  v-for="value in [10, 25, 100]"
                  :key="value"
                  @click="paginate({ rowsPerPage: value })"
                >
                  <v-list-tile-title>{{ value }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </span>
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { isVisibleInAts } from "@/store/client/applications";

export default {
  props: {
    filters: { type: Object },
    loading: { type: Boolean, default: false },
    loadingIndicator: { type: Boolean, default: true },
  },

  created() {
    this.$store.dispatch("client/applications/updateFilters", {
      ...this.filters,
      ...this.params,
    });
  },

  data() {
    return {
      params: {},
      hideIncomplete: true,
    };
  },

  computed: {
    ...mapState("client/applications", {
      active: "active",
      total: "total",
      page: "page",
      per: "rowsPerPage",
      storeFilters: "filters",
    }),

    ...mapGetters("client/applications", {
      hasHidden: "hasHidden",
      progress: "progress",
    }),

    ...mapState("client/opportunities", {
      counts: "counts",
    }),

    applications() {
      if (this.hideIncomplete) {
        return this.active.filter((application) => {
          const inPhase = application.status === this.storeFilters.status;

          return inPhase && isVisibleInAts(application);
        });
      }
      return this.active.filter(
        (application) => application.status === this.storeFilters.status
      );
    },

    pages() {
      return Math.ceil(this.total / this.per);
    },

    from() {
      return this.total > 0 ? 1 + (this.page - 1) * this.per : 0;
    },

    to() {
      return Math.min(this.per * this.page, this.total);
    },

    isFiltered() {
      const irrelevantFilters = ["status"];

      return (
        Object.keys(this.storeFilters).filter(
          (f) => irrelevantFilters.indexOf(f) < 0
        ).length > 0
      );
    },
  },

  watch: {
    filters: {
      handler: function (value) {
        this.$store.dispatch("client/applications/updateFilters", { ...value });
      },
      deep: true,
    },
  },

  methods: {
    paginate(change) {
      if (change) {
        if (change && change.rowsPerPage) Object.assign(change, { page: 1 });
        Object.assign(this.params, change);
        this.$store.dispatch("client/applications/updateFilters", {
          ...change,
        });
      }
    },

    async fetch() {
      this.$store.commit("client/applications/clearSelected");
      await this.$store.dispatch("admin/applications/load", {
        opportunity: Vue.$route.params.id,
      });
      // Pass filters from URL to store
      this.$store.dispatch("client/applications/updateFilters", {
        ...this.filters,
        ...this.params,
      });
    },
  },
};
</script>
